import { Document } from 'modules/common/interfaces/documents';
import { MembershipTier } from 'modules/common/types';

interface Attributes {
    ein_before_llc: boolean;
    is_community_property: boolean;
    wet_ink_signature_required: boolean;
    online_formation_allowed: boolean;
    naics_code_required: boolean;
    publication_required: boolean;
    state_scorp_form_required: boolean;
    business_license_required: boolean;
    passthrough_tax_eligible: boolean;
}

interface Content {
    articles_of_organization_name: string;
    additional_filing_doc_name: string;
    federal_scorp_fax_number: string;
    state_scorp_fax_number: string;
    sos_business_name_check_website: string;
    llc_filing_website: string;
}

interface StateOfInc {
    id: string;
    is_servicing: boolean;
    attributes: Attributes;
    content: Content;
    name: string;
}

interface Business {
    member: number;
    state_of_inc: StateOfInc;
    s_corp_election_date: string;
    preferred_s_election_date?: string | null;
    naics_business_activity: string;
    naics_code: string;
    activity_details: {
        [key: string]: {
            answer1: string;
            answer2: string;
            answer3?: string;
            answer4?: string;
            answer5?: string;
        };
    };
    industry: string;
    profession: string;
    business_context_description: string;
    require_special_filing: boolean;
}

interface OnboardingData {
    member_id: number;
    publication_preference: string;
    insurance_provider: string;
    insturance_policy_number: string;
}

interface FirstPayroll {
    payroll_uuid: string;
    payroll_scheduled_date: string;
    payroll_start_date: string;
    payroll_end_date: string;
    payroll_total_amount: string;
    payroll_processed: boolean;
    payroll_cancelled: boolean;
}

interface KickoffQuestionnaire {
    registered_payroll_taxes_in_state: boolean;
    running_payroll: boolean;
}

export enum MemberStatus {
    PENDING = '---',
    CONFIRMED = 'OK',
    EMPLOYEE = 'EMP',
    OFFBOARDED = 'OFF',
    PAYMENT_UPDATE = 'payment_update',
    PAYMENT_ERROR = 'payment_error',
}

export interface Address {
    street: string;
    unit: string;
    city: string;
    state: string;
    zipcode: string;
    // Derived from the zipcode unless somebody corrects it, so only the forms that offer a
    // county field send it. Omitting the key leaves a stored correction alone; sending an
    // empty string hands the county back to the zipcode.
    county?: string;
}

export interface Member {
    employees_amount: number;
    employees_states: string[];
    id: number;
    business: Business;
    onboarding_data?: OnboardingData;
    first_payroll?: FirstPayroll;
    kickoff_questionnaire?: KickoffQuestionnaire;
    state_registration_document?: Document;
    payroll_service_embedded_status?: string;
    bookkeeping_provider?: string;
    uuid: string;
    created: string;
    updated: string;
    sfaccountid?: string;
    sfcontactid?: string;
    sfopportunityid?: string;
    fullname: string;
    nickname: string;
    preferred_name: string;
    has_gusto_client?: boolean;
    first_name: string;
    last_name: string;
    legal_first_name: string;
    legal_middle_name: string;
    legal_last_name: string;
    dob: string;
    citizenship: string;
    citizenshipcode: string;
    email: string;
    personalphonenumber: string;
    businessname: string;
    federal_business_name: string;
    incorporation: string;
    incsuffix: string;
    primaryactivity: string;
    primaryactivityother: string;
    // Null until the entity exists: set at formation (Confirm LLC) for SP-* members,
    // or from the member's entity docs for takeover members.
    establisheddate: string | null;
    entitynumber: string;
    registeredagent: string;
    stateofoperation: string;
    businessphonenumber: string;
    status: MemberStatus;
    usertype: string;
    cohortdate?: string;
    firstpayment: string;
    lastpayment?: string;
    mainstep: string;
    substep: string;
    stepnumber: number;
    discountcode: string;
    accounting_advisor: string;
    stripecustomerid: string;
    stripesubscriptionid: string;
    stripelast4: string;
    business_address: Address | null;
    mailing_address: Address | null;
    home_address: Address | null;
    consultingdescription: string;
    servicetype: string;
    othertype: string;
    additionalshareholdername: string;
    additionalshareholdershare: number;
    additionalshareholdersameaddress: boolean;
    taxation: string;
    annual_salary: string;
    salary_update_due: boolean;
    advised_salary_low?: string;
    advised_salary_high?: string;
    user: number;
    job_title?: string;
    enrollment_manager?: string;
    member_relationship_manager?: string;
    form_cp261_status?: string;
    training_call_date?: string;
    SSN?: string;
    EIN?: string;
    moving_out_state?: boolean;
    moving_to_state?: string;
    pronouns?: string[];
    one_line_business_address?: string;
    one_line_home_address?: string;
    one_line_mail_address?: string;
    // TODO :(@eng-onboarding) llc_tier_enabled is temporary and should be removed once the official LLC Tier product is built
    llc_tier_enabled?: boolean;
    membership_tier: MembershipTier;
    application_flow_name?: string;
    stage?: 'onboarding' | 'launchpad' | 'activated' | 'offboarded';
    onboarding_completed_at?: string;
    offboarding_date?: string;
    vertical?: 'nil' | 'other' | null;
    partner_lander_slug?: string | null;
    initial_number_w2_employees?: number | null;
}

export interface MemberError {
    detail: string;
}

// Salesforce checkout waives the BE appends to the member detail for sales admins.
export interface MemberSalesforceWaives {
    back_books_waived?: number;
    is_bsu_waived?: boolean;
}

export type MemberWithSalesforceWaives = Member & MemberSalesforceWaives;

export interface MemberParams {
    // Asks the BE to append the Salesforce checkout waives (`MemberSalesforceWaives`) to the member payload.
    extend_salesforce_flag?: boolean;
}

// The response only carries the waives when the request opted in with `extend_salesforce_flag: true`.
export type MemberResponse<TParams extends MemberParams = MemberParams> = TParams['extend_salesforce_flag'] extends true
    ? MemberWithSalesforceWaives
    : Member;
